import { useEffect, useRef, useState } from 'react';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  Flex,
  Box,
  Input,
  Checkbox,
  useDisclosure,
  useOutsideClick,
} from '@chakra-ui/react';
import {
  format,
  startOfWeek,
  endOfWeek,
  addDays,
  addWeeks,
  startOfDay,
  isBefore,
  isAfter,
  isWithinInterval,
  isSameDay,
} from 'date-fns';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import './index.css';
import { KQuickCalendar } from './KQuickCalendar';
import { KText } from '@/ui-library/components/text';
import { useShowToast } from '@/hooks/useShowToast';
import moment from 'moment';
import { useCallback } from 'react';
import { Button } from '@kula-ai/components';

const today = moment().toDate();

/** Returns true if the date is disabled by any of the matchers (same shape as DayPicker disabled prop). */
function isDateDisabled(
  date: Date,
  matchers: unknown[] | undefined
): boolean {
  if (!matchers?.length) return false;
  const d = startOfDay(date);
  return matchers.some((matcher: any) => {
    if (matcher instanceof Date) return isSameDay(d, matcher);
    if (matcher?.before !== undefined && matcher?.before !== null)
      return isBefore(d, startOfDay(matcher.before));
    if (matcher?.after !== undefined && matcher?.after !== null)
      return isAfter(d, startOfDay(matcher.after));
    if (
      matcher?.from !== undefined &&
      matcher?.from !== null &&
      matcher?.to !== undefined &&
      matcher?.to !== null
    )
      return isWithinInterval(d, { start: matcher.from, end: matcher.to });
    return false;
  });
}

const defaultCalendarShortCutOptions = [
  { label: 'Tomorrow', id: 'tomorrow' },
  { label: 'This week', id: 'this_week' },
  { label: 'Next week', id: 'next_week' },
  { label: 'Next 2 weeks', id: 'next_2_weeks' },
  { label: 'Next 3 weeks', id: 'next_3_weeks' },
];

export type KCustomDateRangeOnChangePayload = {
  id: number;
  from: Date | null;
  to: Date | null;
  skipWeekend?: boolean;
};

interface KCustomDateRangeProps {
  triggerElement: React.ReactNode;
  selected: {
    from: Date | null;
    to: Date | null;
    id: number;
    skipWeekend?: boolean;
  };
  onChange: (range: KCustomDateRangeOnChangePayload) => void;
  saveBtnLabel?: string;
  calendarShortCutOptions?: { label: string; id: string }[];
  timezone?: string;
  disabledDates?: any;
  startWithPreviousMonth?: boolean;
  resetRangeOnSelection?: boolean;
  ensureSelectionIsUnique?: boolean;
  isEdit?: boolean;
  editCaseDisabledDates?: any;
  triggerContainerStyles?: any;
  showTextBoxForSelectedItem?: boolean;
  enableSkipWeekend?: boolean;
}

export const KCustomDateRange = ({
  triggerElement,
  selected = { from: null, to: null, id: 0 },
  onChange = (range: { from: Date | null; to: Date | null }) => {},
  saveBtnLabel = 'Apply',
  calendarShortCutOptions = defaultCalendarShortCutOptions,
  timezone = '',
  disabledDates = [{ before: today }], // { after: today },
  startWithPreviousMonth = false,
  resetRangeOnSelection = false,
  ensureSelectionIsUnique = false,
  isEdit = false,
  editCaseDisabledDates = [{ before: today }],
  triggerContainerStyles = { h: '38px' },
  showTextBoxForSelectedItem = false,
  enableSkipWeekend = false,
}: KCustomDateRangeProps) => {
  const toast = useShowToast();
  const ref = useRef();
  // @ts-ignore
  const { isOpen, onOpen, onClose } = useDisclosure(false);
  const previousMonth = new Date(today.getTime());
  previousMonth.setDate(startWithPreviousMonth ? 0 : 1);
  const [month, setMonth] = useState(previousMonth);
  const [selectedQuickCalendarItem, setSelectedQuickCalendarItem] = useState({
    id: 0,
  });

  const [range, setRange] = useState({
    from: selected?.from || today,
    to: selected?.to || today,
  });

  const [skipWeekend, setSkipWeekend] = useState(false);
  // Sync only when popover first opens (not when already open). Keeps existing flows working:
  // - Controlled (e.g. DateRangeSelection, availability row): open → sync from selected; presets not overwritten on re-render.
  // - Static default (e.g. "Add another date range"): open → sync default once; preset clicks (Next 2 weeks, etc.) stick.
  const wasOpenRef = useRef(false);

  useEffect(() => {
    const justOpened = isOpen && !wasOpenRef.current;
    wasOpenRef.current = isOpen;
    if (justOpened) {
      const from = selected?.from ?? null;
      const to = selected?.to ?? null;
      const nextFrom = from || today;
      const nextTo = to || today;
      setRange({ from: nextFrom, to: nextTo });
      setMonth(nextFrom);
      if (enableSkipWeekend && selected?.skipWeekend !== undefined) {
        setSkipWeekend(selected.skipWeekend);
      }
    }
  }, [
    isOpen,
    enableSkipWeekend,
    selected?.from,
    selected?.to,
    selected?.skipWeekend,
  ]);

  useEffect(() => {
    // When the popover is closed and resetRangeOnSelection is true,
    // reset the date range selection. If in edit mode, reset to the current selection or today.
    // Otherwise, clear the selection (set both from and to to null).
    if (!isOpen && resetRangeOnSelection) {
      if (isEdit) {
        setRange({
          from: selected?.from || today,
          to: selected?.to || today,
        });
      } else {
        setRange({
          from: null,
          to: null,
        });
      }
    }
  }, [isOpen, resetRangeOnSelection, selected, isEdit]);

  const getDisabledDates = useCallback((): any[] => {
    // Returns the list of disabled dates for the date picker.
    // If editing an existing range, remove the current selection from the disabled list to allow re-selection,
    // then always add a rule to disable all dates before today AND today itself ({ before } is exclusive).
    const todayStart = startOfDay(new Date());
    if (isEdit) {
      const currentSelectionRemoved = disabledDates.filter(
        (disabled) => disabled.id !== selected.id
      );
      return [...currentSelectionRemoved, { before: todayStart }, todayStart];
    }
    return [...disabledDates, { before: todayStart }, todayStart];
  }, [disabledDates, selected, isEdit]);

  const hasUniqueDates = (selectedRange: {
    from: Date | null;
    to: Date | null;
  }) => {
    const uniqueFailedRange = disabledDates?.filter(
      (disabled: { from: Date | null; to: Date | null; id?: number }) => {
        // Skip validation if we're editing and the IDs match
        if (isEdit && disabled.id === selected.id) {
          return false;
        }

        if (
          !selectedRange.from ||
          !selectedRange.to ||
          !disabled.from ||
          !disabled.to
        )
          return false;

        // Check for overlap
        return (
          selectedRange.from <= disabled.to && selectedRange.to >= disabled.from
        );
      }
    );

    const isUnique = uniqueFailedRange.length === 0;

    return {
      uniqueFailedRange,
      unique: isUnique,
    };
  };

  const showUniqueSelectioMsg = (
    uniqueFailedRange: { from: Date; to: Date }[]
  ) => {
    const firstItemArray = uniqueFailedRange.slice(0, 1);
    let errorMessage = firstItemArray
      .map((itr) =>
        itr.from === itr.to
          ? `${format(itr.from, 'EEEE, d MMMM yyyy')}`
          : `${format(itr.from, 'dd/MM/yyyy')} - ${format(
              itr.to,
              'dd/MM/yyyy'
            )}`
      )
      .join(', ');
    toast({
      description: (
        <>
          <p>
            <Box fontWeight={550}>
              Selected range overlaps with an existing selection:{' '}
            </Box>
            <KText fontSize={'12px'} color="#fff">
              {errorMessage}
            </KText>
          </p>
          <p>Please choose a different range.</p>
        </>
      ),
      status: 'error',
      position: 'bottom-left',
      duration: 5000,
      isClosable: true,
    });
  };

  const onApply = () => {
    if (ensureSelectionIsUnique) {
      const { uniqueFailedRange, unique } = hasUniqueDates(range as any);
      if (!unique) {
        showUniqueSelectioMsg(uniqueFailedRange);
        return;
      }
    }
    const id = selected?.id ?? 0;
    onChange({
      id,
      from: range?.from ?? null,
      to: range?.to ?? null,
      ...(enableSkipWeekend && { skipWeekend: skipWeekend }),
    });
    onClose();
  };

  const onChangeQuickCalender = (selectedItem) => {
    setSelectedQuickCalendarItem(selectedItem);
    let from = today;
    let to = today;

    switch (selectedItem?.id) {
      case 'tomorrow':
        from = to = addDays(today, 1);
        break;

      case 'this_week': {
        // Start from first enabled day in this week (respects disabledDates, e.g. when today is disabled)
        const weekEnd = endOfWeek(today);
        const matchers = getDisabledDates();
        from = today;
        while (
          from <= weekEnd &&
          isDateDisabled(from, matchers)
        ) {
          from = addDays(from, 1);
        }
        if (from > weekEnd) from = weekEnd;
        to = weekEnd;
        break;
      }

      case 'next_week': {
        const nextWeekStart = startOfWeek(addWeeks(today, 1));
        const nextWeekEnd = endOfWeek(addWeeks(today, 1));
        from = nextWeekStart;
        to = nextWeekEnd;
        break;
      }

      case 'next_2_weeks': {
        const weekStart = startOfWeek(addWeeks(today, 1));
        const weekEnd = endOfWeek(addWeeks(today, 2));
        from = weekStart;
        to = weekEnd;
        break;
      }

      case 'next_3_weeks': {
        const weekStart = startOfWeek(addWeeks(today, 1));
        const weekEnd = endOfWeek(addWeeks(today, 3));
        from = weekStart;
        to = weekEnd;
        break;
      }

      default:
        return;
    }

    setRange({ from, to });
    setMonth(from);
  };

  const setDate = (range) => {
    if (!range) {
      // When selection is cleared (e.g. double-tap), do not default to today so
      // today cannot be accidentally selected when it is disabled.
      setRange({ from: null, to: null });
      return;
    }
    if (!range?.from) {
      setRange({ ...range, from: range?.to });
      return;
    }

    if (!range?.to) {
      setRange({ ...range, to: range?.from });
      return;
    }

    setRange(range);
  };

  useOutsideClick({
    ref: ref,
    handler: () => onClose(),
  });

  return (
    <Box bg="white" ref={ref} {...triggerContainerStyles}>
      <Popover
        placement={window.innerWidth < 1600 ? 'auto' : 'bottom-start'}
        strategy="fixed"
        isOpen={isOpen}
      >
        {/* @ts-ignore */}
        <PopoverTrigger>
          <Flex onClick={isOpen ? onClose : onOpen}>{triggerElement}</Flex>
        </PopoverTrigger>
        <PopoverContent w="800px">
          <Flex w="800px" flexDirection="column">
            <Flex flex="1" minH="0">
              <Box width="150px" borderRight="1px solid #EAECF0">
                <KQuickCalendar
                  options={calendarShortCutOptions}
                  onChange={onChangeQuickCalender}
                  selected={selectedQuickCalendarItem as any}
                />
              </Box>
              <Box flex="1" minH="340px">
                <DayPicker
                  mode="range"
                  numberOfMonths={2}
                  month={month}
                  onMonthChange={setMonth}
                  selected={range}
                  onSelect={setDate}
                  disabled={getDisabledDates()}
                />
              </Box>
            </Flex>
            <Flex
              borderTop="1px solid #EAECF0"
              height="64px"
              alignItems="center"
              px="16px"
              justifyContent="space-between"
              flexShrink={0}
            >
              <Flex flex="1" alignItems="center" gap="16px">
                {enableSkipWeekend && (
                  <Checkbox
                    isChecked={skipWeekend}
                    onChange={(e) => setSkipWeekend(e.target.checked)}
                  >
                    <KText color="secondary-900">Skip weekends</KText>
                  </Checkbox>
                )}
                {showTextBoxForSelectedItem && (
                  <>
                    <Input
                      width="136px"
                      value={format(new Date(range?.from), 'dd/MMM/yyyy')}
                      pointerEvents="none"
                      onChange={() => {}}
                    />
                    <KText px="8px">-</KText>
                    <Input
                      width="136px"
                      value={format(new Date(range?.to), 'dd/MMM/yyyy')}
                      pointerEvents="none"
                      onChange={() => {}}
                    />
                  </>
                )}
              </Flex>
              <Flex gap="8px">
                <Button variant="secondary" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  disabled={range?.from === null && range?.to === null}
                  onClick={onApply}
                  variant="primary"
                >
                  {saveBtnLabel}
                </Button>
              </Flex>
            </Flex>
          </Flex>
        </PopoverContent>
      </Popover>
    </Box>
  );
};
